// Facebook (Meta) click/browser identifiers for CAPI attribution.
//
// Meta needs _fbc (click id) and/or _fbp (browser id) to attribute a
// server-side (Conversions API) event to the specific ad that drove the click.
//
// EINWILLIGUNG (Datenschutz-Prüfung 02.10.2026, KA-22): Cookies auf dem Gerät
// und die Weitergabe an Meta brauchen die Marketing-Einwilligung (TTDSG §25,
// DSGVO). Bis 02.10. wurden `_fbc`/`_fbp` hier auch OHNE Einwilligung gesetzt
// und gesendet. Jetzt wie bei Google (captureGclid): Die fbclid der Landing-URL
// wird nur im Speicher gehalten; das Cookie entsteht erst mit Einwilligung
// (beim Laden mit gespeicherter Einwilligung oder beim Opt-in im Banner →
// persistFbc). Ohne Einwilligung liefert getFacebookTrackingData keine IDs und
// marketing_consent: false, die Edge Functions senden dann nichts an Meta.

import { hatMarketingEinwilligung } from "@/lib/consentSpeicher";

const FBC_COOKIE = "_fbc";
const FBP_COOKIE = "_fbp";
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365;

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
}

/**
 * Build a Meta `_fbc` value from a raw fbclid.
 * Format per Meta spec: fb.<subdomainIndex>.<creationTimeMs>.<fbclid>
 * We use subdomainIndex 1 (the convention the Pixel uses for a single domain).
 */
export function buildFbc(fbclid: string, nowMs: number): string {
  return `fb.1.${nowMs}.${fbclid}`;
}

// In-Memory-Stash der Landing-fbclid (SPA: überlebt Navigationen ohne Reload).
let stashedFbclid: string | undefined;
let stashedAtMs: number | undefined;

/**
 * Capture the `fbclid` query param from the current landing URL. Immer nur im
 * Speicher; als `_fbc`-Cookie erst mit Marketing-Einwilligung (sonst später über
 * persistFbc nach dem Opt-in im Banner). Idempotent, wirft nie.
 */
export function captureFbclid(hasMarketingConsent: boolean, nowMs: number = Date.now()): void {
  if (typeof window === "undefined") return;
  try {
    const fbclid = new URLSearchParams(window.location.search).get("fbclid");
    if (fbclid && !stashedFbclid) {
      stashedFbclid = fbclid;
      stashedAtMs = nowMs;
    }
    if (hasMarketingConsent) persistFbc();
  } catch {
    // Never break page load over tracking.
  }
}

/**
 * Schreibt die gestashte fbclid als `_fbc`-Cookie, sofern noch keins existiert
 * (das Pixel kann ein frischeres gesetzt haben). Nur nach Marketing-Einwilligung
 * aufrufen.
 */
export function persistFbc(): void {
  try {
    if (!stashedFbclid) return;
    if (readCookie(FBC_COOKIE)) return; // don't clobber an existing _fbc
    writeCookie(FBC_COOKIE, buildFbc(stashedFbclid, stashedAtMs ?? Date.now()), ONE_YEAR_SECONDS);
  } catch {
    // Never break over tracking.
  }
}

/**
 * Build a Meta `_fbp` (browser id) value.
 * Format per Meta spec: fb.<subdomainIndex>.<creationTimeMs>.<randomNumber>
 */
export function buildFbp(nowMs: number): string {
  const random = Math.floor(Math.random() * 1e10);
  return `fb.1.${nowMs}.${random}`;
}

/** Was an Edge Functions mitgeht: Meta-IDs nur mit Einwilligung, plus der Einwilligungs-Stand. */
export type FacebookTrackingData = { fbc?: string; fbp?: string; marketing_consent: boolean };

/**
 * Read the Facebook click/browser identifiers for CAPI user_data, zusammen mit
 * dem Stand der Marketing-Einwilligung (`marketing_consent`). Ohne Einwilligung:
 * keine IDs, kein Cookie, `marketing_consent: false`. Die Edge Functions geben
 * das Feld an meta-capi-event weiter, das ohne Einwilligung nichts an Meta sendet.
 *
 * @param opts.ensureFbp - Mit Einwilligung und ohne vorhandenes `_fbp` eines
 *   erzeugen, damit ein serverseitiges Event eine Browser-ID hat. Nur beim
 *   Nutzer-Schritt (Buchung, Bewerbung, Kauf) übergeben, nie beim Seitenaufruf.
 */
export function getFacebookTrackingData(
  opts?: { ensureFbp?: boolean },
  consent: boolean = hatMarketingEinwilligung(),
): FacebookTrackingData {
  if (!consent) return { marketing_consent: false };
  const data: FacebookTrackingData = { marketing_consent: true };
  const fbc = readCookie(FBC_COOKIE);
  let fbp = readCookie(FBP_COOKIE);
  if (!fbp && opts?.ensureFbp) {
    fbp = buildFbp(Date.now());
    writeCookie(FBP_COOKIE, fbp, ONE_YEAR_SECONDS);
  }
  if (fbc) data.fbc = fbc;
  if (fbp) data.fbp = fbp;
  return data;
}
